// LoadingState — the portal-wide standard loading / refresh indicators.
//
// Extracted from the InvestigationsAvoidedDashboard pattern (v1.79.x) and adopted
// as the standard across every data page so loading states look and behave the
// same everywhere. Three primitives, each maps to one part of that pattern:
//
//   <RefreshButton>     — the refresh control; spinner + "Refreshing…" while loading
//   <RefreshingBanner>  — blue inline banner shown when RELOADING over existing data
//   <LoadingCard>       — centered full-card spinner shown when there's NO data yet
//
// Usage on a typical list/dashboard page:
//   <RefreshButton loading={loading} onClick={load} />
//   <RefreshingBanner show={loading && hasData} message="Refreshing alerts…" />
//   {loading && !hasData
//     ? <LoadingCard message="Loading alerts…" />
//     : <div className={dataFadeClass(loading)}>…data…</div>}
//
// `dataFadeClass(loading)` returns the transition-opacity fade used to dim
// existing data while a refresh is in flight.

import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

// Dim + lock existing data while a refresh is in flight.
export function dataFadeClass(loading) {
  return `transition-opacity ${loading ? 'opacity-40 pointer-events-none' : 'opacity-100'}`;
}

// The refresh control. Defaults to the .btn-secondary chrome used on admin pages;
// pass `className` to override (e.g. the borderless ghost button on AlertsPage).
export function RefreshButton({
  loading = false,
  onClick,
  disabled = false,
  label = 'Refresh',
  loadingLabel = 'Refreshing…',
  className = 'btn-secondary flex items-center gap-1.5 text-xs',
  iconClassName = 'h-3.5 w-3.5',
  title,
  ...rest
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      className={className}
      {...rest}
    >
      <ArrowPathIcon className={`${iconClassName} ${loading ? 'animate-spin' : ''}`} />
      {loading ? loadingLabel : label}
    </button>
  );
}

// Blue inline banner — show ONLY when reloading over data already on screen.
// Renders nothing when `show` is false so callers can inline it unconditionally.
export function RefreshingBanner({ show = true, message = 'Refreshing…', className = '' }) {
  if (!show) return null;
  return (
    <div className={`mb-4 flex items-center gap-2 text-sm text-blue-200 bg-blue-900/20 border border-blue-700/40 rounded-lg px-4 py-2.5 ${className}`}>
      <ArrowPathIcon className="h-4 w-4 animate-spin flex-shrink-0" />
      <span>{message}</span>
    </div>
  );
}

// Centered full-card spinner — show when there's no data yet (initial / empty load).
export function LoadingCard({ message = 'Loading…', className = '' }) {
  return (
    <div className={`card py-12 flex flex-col items-center gap-3 ${className}`}>
      <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
      <span className="text-gray-500 text-sm">{message}</span>
    </div>
  );
}

export default { RefreshButton, RefreshingBanner, LoadingCard, dataFadeClass };
